import type { ReactNode } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';

export interface ConfirmModalProps {
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'primary';
  isPending?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ConfirmModal({
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  isPending = false,
  onCancel,
  onConfirm,
}: ConfirmModalProps) {
  const confirmCls =
    tone === 'danger'
      ? 'inline-flex items-center gap-1.5 rounded bg-danger-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-danger-700 disabled:opacity-60'
      : 'inline-flex items-center gap-1.5 rounded bg-accent-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-60';
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-lg border border-surface-border bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-surface-border px-5 py-4">
          <h2 className="flex items-center gap-2 text-base font-semibold text-ink-primary">
            {tone === 'danger' && <AlertTriangle className="h-4 w-4 text-danger-600" />}
            {title}
          </h2>
        </div>
        <div className="space-y-3 px-5 py-4 text-sm text-ink-secondary">{body}</div>
        <div className="flex justify-end gap-2 border-t border-surface-border px-5 py-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="rounded border border-surface-border px-3 py-1.5 text-sm font-medium text-ink-primary hover:bg-surface-muted/50 disabled:opacity-60"
          >
            {cancelLabel}
          </button>
          <button type="button" className={confirmCls} onClick={onConfirm} disabled={isPending}>
            {isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
